/**
 * Checkout mini-FAQ — the five questions the concierge desk actually gets at
 * the review step, answered in the knowledge base's own words.
 *
 * Why it exists: the review step is where ~90% of checkout attempts stall
 * (Ezra digests, Aug–Sep 2026). Customers see the full price and schedule
 * and leave with questions they never ask. This answers them where the
 * hesitation happens — a compact copy under the payment schedule — and again
 * at the bottom of the page.
 *
 * Content lives in checkoutFaqContent.js (parity-tested against the desk's
 * knowledge base); this file is the accordion only. Where checkout offers Faye
 * travel insurance, Review passes `insuranceOffered` and a travel insurance
 * question joins the list — only while the Faye launch flag is on.
 */
import { useState } from 'react';
import PropTypes from 'prop-types';
import { ChevronDown, HelpCircle } from 'lucide-react';
import { cn } from '@/lib/utils';
import { trackEvent } from '@/lib/activityTracker';
import { BRAND_NESTED_PANEL } from '@/constants/brandColors';
import { checkoutFaqItems } from './checkoutFaqContent';


function FaqItem({ item, open, onToggle, compact }) {
  return (
    <div className="border-t border-white/10 first:border-t-0">
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        aria-controls={`faq-${item.id}`}
        className={cn(
          'flex w-full items-center justify-between gap-3 text-left text-white/85 hover:text-white transition-colors',
          compact ? 'py-3 text-[14px]' : 'py-4 text-[15px]',
        )}
      >
        <span className="font-normal leading-snug">{item.q}</span>
        <ChevronDown
          className={cn('h-4 w-4 shrink-0 text-white/45 transition-transform duration-200', open && 'rotate-180')}
          aria-hidden
        />
      </button>
      {open && (
        <p
          id={`faq-${item.id}`}
          className={cn('text-white/60 font-normal leading-relaxed pb-4 pr-6', compact ? 'text-[13px]' : 'text-[14px]')}
        >
          {item.a}
        </p>
      )}
    </div>
  );
}

/**
 * @param {object} props
 * @param {'compact'|'full'} [props.variant] compact = under the payment schedule; full = bottom-of-page card body
 * @param {string} [props.placement] telemetry label ("schedule" | "bottom")
 * @param {boolean} [props.insuranceOffered] checkout offers Faye travel insurance on this
 *   trip — adds the travel insurance question, only while the Faye launch flag is on
 */
export default function CheckoutFaq({ variant = 'full', placement = 'bottom', className, insuranceOffered = false }) {
  const [openId, setOpenId] = useState(null);
  const compact = variant === 'compact';
  const items = checkoutFaqItems({ insuranceOffered });

  const toggle = (item) => {
    const next = openId === item.id ? null : item.id;
    setOpenId(next);
    if (next) trackEvent('checkout_faq_opened', { question: item.id, placement });
  };

  return (
    <section
      aria-label="Common questions before you book"
      className={cn(compact ? cn(BRAND_NESTED_PANEL, 'px-4') : 'px-6 py-2', className)}
    >
      {compact && (
        <div className="flex items-center gap-2 pt-3 pb-1 text-[11px] uppercase tracking-[0.12em] text-white/45 font-medium">
          <HelpCircle className="h-3.5 w-3.5" aria-hidden />
          Questions before you book?
        </div>
      )}
      {items.map((item) => (
        <FaqItem key={item.id} item={item} open={openId === item.id} onToggle={() => toggle(item)} compact={compact} />
      ))}
    </section>
  );
}

FaqItem.propTypes = {
  item: PropTypes.shape({ id: PropTypes.string.isRequired, q: PropTypes.string.isRequired, a: PropTypes.string.isRequired }).isRequired,
  open: PropTypes.bool.isRequired,
  onToggle: PropTypes.func.isRequired,
  compact: PropTypes.bool,
};

CheckoutFaq.propTypes = {
  variant: PropTypes.oneOf(['compact', 'full']),
  placement: PropTypes.string,
  className: PropTypes.string,
  insuranceOffered: PropTypes.bool,
};
